import type { FC } from 'react';
import { Suspense, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import type { OptionValue } from 'types/shared';
import RuleFilterInput from './RuleFilterInput';
import type { Option } from 'types/shared';
import { isEmpty } from 'lodash-es';
import { RuleEditAutocomplete, RuleEditAutocompleteMultiple } from './index';
import useFilterStore from '../hooks/useFilterStore';
import { useCurrentNetwork } from '../../../hooks';
import FlowAutocomplete from './FlowAutocomplete';
import useRulePageMode from '../hooks/useRulePageMode';

interface IProps {
  groupIndex: number;
  index: number;
  values: Option[];
}

const FilterValue: FC<IProps> = observer(({ groupIndex, index, values }) => {
  const { isRead } = useRulePageMode();
  const filterStore = useFilterStore();
  const network = useCurrentNetwork();
  const filter = filterStore.getFilter(groupIndex, index);

  const handleChangeValue = useCallback(
    (value: OptionValue | OptionValue[]) => {
      const formattedValue = Array.isArray(value) ? value.map(String) : String(value);

      filterStore.updateValue(groupIndex, index, formattedValue);
    },
    [filterStore, groupIndex, index]
  );

  if (network === 'facebook' && filter?.field === 'flow') {
    return (
      <Suspense fallback={<span>Loading...</span>}>
        <FlowAutocomplete
          allSelectable={false}
          value={filter?.value as OptionValue}
          InputProps={{
            placeholder: 'Value',
            disabled: isRead,
          }}
          onChange={handleChangeValue}
          id="value"
        />
      </Suspense>
    );
  }

  if (!isEmpty(values)) {
    if (network === 'google') {
      return (
        <RuleEditAutocomplete
          value={filter?.value as OptionValue}
          InputProps={{
            placeholder: 'Value',
            disabled: isRead,
          }}
          onChange={handleChangeValue}
          id="value"
          options={values}
        />
      );
    }

    return (
      <RuleEditAutocompleteMultiple
        allSelectable={false}
        value={filter?.value as OptionValue[]}
        InputProps={{
          placeholder: 'Value',
          disabled: isRead,
        }}
        onChange={handleChangeValue}
        id="value"
        options={values}
      />
    );
  }

  return (
    <RuleFilterInput
      placeholder="Value"
      value={filterStore.getFilter(groupIndex, index)?.value ?? ''}
      onChange={handleChangeValue}
    />
  );
});

export default FilterValue;
